import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Link as Link1 } from 'react-scroll';
import { useTranslation } from 'react-i18next';

import logoDark from '../assets/images/logo-deprem.png';
import logoLight from '../assets/images/logo-deprem.png';
import { TfiWorld } from 'react-icons/tfi';

const SUPPORTED_LANGUAGES = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ca', label: 'Catalan' },
  { code: 'zh-Hant-TW', label: 'Chinese, Traditional (Taiwan)' }, // Geleneksel Çince (Tayvan)
  { code: 'zh-Hans', label: 'Chinese' }, // Basitleştirilmiş Çince
  { code: 'hr', label: 'Croatian' },
  { code: 'cs', label: 'Czech' },
  { code: 'da', label: 'Danish' },
  { code: 'nl', label: 'Dutch' },
  { code: 'fi', label: 'Finnish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'el', label: 'Greek' },
  { code: 'he', label: 'Hebrew' },
  { code: 'hi', label: 'Hindi' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'id', label: 'Indonesian' },
  { code: 'it', label: 'Italian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ms', label: 'Malay' },
  { code: 'nb', label: 'Norwegian Bokmål' }, // Bokmål
  { code: 'pl', label: 'Polish' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)' }, // Brezilya Portekizcesi
  { code: 'pt-PT', label: 'Portuguese (Portugal)' }, // Portekiz Portekizcesi
  { code: 'ro', label: 'Romanian' },
  { code: 'ru', label: 'Russian' },
  { code: 'sk', label: 'Slovak' },
  { code: 'es', label: 'Spanish' },
  { code: 'sv', label: 'Swedish' },
  { code: 'th', label: 'Thai' },
  { code: 'uk', label: 'Ukrainian' },
  { code: 'vi', label: 'Vietnamese' },
];

export default function Navbar({
  navLight,
  bgLight,
  navCenter,
}: {
  navLight: boolean;
  playBtn: boolean;
  bgLight: boolean;
  navCenter: boolean;
}) {
  const [menu, setMenu] = useState(false);
  const [scroll, setScroll] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { t, i18n, ready } = useTranslation();

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handlerScroll = () => {
      setScroll(window.scrollY > 50);
    };
    window.addEventListener('scroll', handlerScroll);
    return () => window.removeEventListener('scroll', handlerScroll);
  }, []);

  // NOT: Bu useEffect'i kaldırdım, çünkü başlangıç dilini 'tr' olarak zorlamak yerine
  // i18n yapılandırmasında (i18n.ts) varsayılan dili ayarlamak daha iyidir.
  // useEffect(() => {
  //   if (i18n.language !== 'tr') {
  //     i18n.changeLanguage('tr');
  //   }
  // }, [i18n]);

  // i18next yüklenmesini beklerken sadece logoyu gösteren sade bir navbar döndür
  if (!ready) {
    return (
      <nav
        className={`navbar ${
          bgLight ? 'bg-white dark:bg-slate-900 shadow dark:shadow-gray-800' : ''
        }`}
        id="navbar"
      >
        <div className="container relative flex flex-wrap items-center justify-center">
          {/* Logo kısmını yüklenme sırasında da gösterelim */}
          <Link className="navbar-brand mx-auto w-full flex justify-center items-center" to="/">
            <span className="relative flex items-center justify-center">
              <img
                src={logoDark}
                className="h-16 sm:h-20 inline-block dark:hidden relative z-10"
                alt="Logo Yükleniyor"
              />
              <img
                src={logoLight}
                className="h-16 sm:h-20 hidden dark:inline-block relative z-10"
                alt="Logo Yükleniyor"
              />
            </span>
          </Link>
          {/* Loading mesajı yerine, navbarı boş bırakıyoruz. */}
        </div>
      </nav>
    );
  }

  // ready=true ise, bu değişkenler güvenle tanımlanabilir
  const currentLang = i18n.language;
  const currentLangLabel =
    SUPPORTED_LANGUAGES.find((lang) => lang.code === currentLang)?.label ||
    currentLang.toUpperCase(); // currentLang artık tanımlı olduğu için güvenli

  return (
    <nav
      className={`navbar ${
        bgLight ? 'bg-white dark:bg-slate-900 shadow dark:shadow-gray-800' : ''
      } ${scroll ? 'is-sticky' : ''}`}
      id="navbar"
    >
      {/* LOGO BÖLÜMÜ (Statik) */}
      {!navLight && (
        <Link className="navbar-brand mx-auto w-full flex justify-center items-center" to="/">
          <span className="relative flex items-center justify-center">
            {/* Pulse efekti */}
            <span className="absolute rounded-full border-2 border-indigo-500 opacity-40 animate-ping h-8 w-8"></span>
            <span className="absolute rounded-full border-2 border-indigo-500 opacity-20 animate-ping h-8 w-8 delay-500"></span>
            <img
              src={logoDark}
              className="h-16 sm:h-20 md:h-28 lg:h-36 inline-block dark:hidden relative z-10"
              alt="Boğaziçi Üniversitesi Kandilli Rasathanesi Deprem Erken Uyarı Uygulaması"
            />
            <img
              src={logoLight}
              className="h-16 sm:h-20 md:h-28 lg:h-36 hidden dark:inline-block relative z-10"
              alt="Boğaziçi Üniversitesi Kandilli Rasathanesi Deprem Erken Uyarı Uygulaması"
            />
          </span>
        </Link>
      )}
      {navLight && (
        <Link className="navbar-brand" to="/">
          <span className="inline-block dark:hidden">
            <img src={logoDark} className="l-dark" alt="" />
            <img src={logoLight} className="l-light" alt="" />
          </span>
          <img src={logoLight} className="hidden dark:inline-block" alt="" />
        </Link>
      )}
      <div className="container relative flex flex-wrap items-center justify-center">
        {/* NAVIGASYON MENÜSÜ */}
        <div
          className={`navigation lg_992:order-1 lg_992:flex justify-center mx-auto ${
            navCenter ? '' : ''
          } ${menu ? '' : 'hidden'}`}
          id="menu-collapse"
        >
          <ul className={`navbar-nav ${navLight ? 'nav-light' : ''}`} id="navbar-navlist">
            <li className="nav-item ms-0">
              <Link1
                className="nav-link"
                activeClass="active"
                spy={true}
                smooth={true}
                duration={500}
                to="home"
              >
                {t('nav.home')} {/* Çeviriyi kullan */}
              </Link1>
            </li>
            <li className="nav-item ms-0">
              <Link1
                className="nav-link"
                activeClass="active"
                spy={true}
                smooth={true}
                duration={500}
                to="features"
              >
                {t('nav.features')} {/* Çeviriyi kullan */}
              </Link1>
            </li>
            <li className="nav-item ms-0">
              <Link1
                className="nav-link"
                activeClass="active"
                spy={true}
                smooth={true}
                duration={500}
                to="screenshot"
              >
                {t('nav.screenshots')} {/* Çeviriyi kullan */}
              </Link1>
            </li>
            <li className="nav-item ms-0">
              <Link1
                className="nav-link"
                activeClass="active"
                spy={true}
                smooth={true}
                duration={500}
                to="faqs"
              >
                {t('nav.faqs')} {/* Çeviriyi kullan */}
              </Link1>
            </li>
            <li className="nav-item ms-0">
              <Link1
                className="nav-link"
                activeClass="active"
                spy={true}
                smooth={true}
                duration={500}
                to="download"
              >
                {t('nav.download')} {/* Çeviriyi kullan */}
              </Link1>
            </li>
            <li className="nav-item ms-0">
              <Link1
                className="nav-link"
                activeClass="active"
                spy={true}
                smooth={true}
                duration={500}
                to="reviews"
              >
                {t('nav.team')} {/* Çeviriyi kullan */}
              </Link1>
            </li>
            <li className="nav-item ms-0">
              <Link1
                className="nav-link"
                activeClass="active"
                spy={true}
                smooth={true}
                duration={500}
                to="contact"
              >
                {t('nav.contact')} {/* Çeviriyi kullan */}
              </Link1>
            </li>
          </ul>
        </div>

        {/* DİL SEÇİM DROPDOWN */}
        <div className="relative lg_992:order-3 md:ms-6 flex items-center">
          {/* Dropdown Tetikleyici Buton */}
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="h-8 px-4 text-[14px] tracking-wider inline-flex items-center justify-center font-medium rounded-full bg-indigo-600 hover:bg-indigo-700 text-white uppercase transition-all duration-300 focus:outline-none"
          >
            <TfiWorld className="me-2 size-4" />
            {currentLangLabel} {/* Şu anki dili göster */}
            <svg
              className={`ms-2 -mr-1 size-4 transition-transform duration-300 ${
                isDropdownOpen ? 'rotate-180' : 'rotate-0'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </button>

          {/* Dropdown Menü İçeriği */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 top-full w-48 bg-white dark:bg-slate-800 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto ring-1 ring-black ring-opacity-5">
              <div className="py-1">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`block w-full text-start px-4 py-2 text-sm transition-colors duration-200 
                        ${
                          currentLang === lang.code
                            ? 'bg-indigo-500 text-white font-semibold'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700'
                        }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
